"use client";

import { useCurrency } from "@/contexts/CurrencyContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { usePathname, useRouter } from "@/i18n/navigation";
import { formatCurrency } from "@/utils/currencyFormatter";
import { dateFormats, getShortDayNames, getShortMonthName } from "@/utils/dateFormatter";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/Button";
import { getLocalizedPath } from "@/utils/pathUtils";

// Constants - mevcut yapıyı bozmadan ekle
const DAYS_TO_SHOW = 30;
const MOBILE_BREAKPOINT = 768;
const TOOLTIP_WIDTH = 300;

export default function BarChartCalendarGraph({
  data,
  origin,
  destination,
  departureCity,
  returnCity,
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { currency } = useCurrency();
  const locale = useLocale();
  const t = useTranslationDetector();
  const tFlight = useTranslationDetector("flight");

  const dayNames = useMemo(() => {
    const days = getShortDayNames(locale, "dd");

    const sunday = days.pop();

    return [sunday, ...days];
  }, [locale]);

  // Response'dan gelen data'yı kullan - null değerleri 0'a çevir - useMemo ile optimize et
  const chartData = useMemo(() => {
    const today = new Date();
    const priceData = data?.slice(0, DAYS_TO_SHOW) || [];

    // Önce tüm datayi oluştur
    const allData = Array.from({ length: DAYS_TO_SHOW }, (_, index) => {
      const date = new Date(today);
      date.setDate(today.getDate() + index);
      const price = priceData[index] ?? 0;

      return {
        date: date.toISOString().split("T")[0],
        price: price,
        month: null,
      };
    });

    // Aylara göre grupla ve her ayın en küçük gününü bul
    const monthFirstIndices = new Map();

    allData.forEach((item, index) => {
      const date = new Date(item.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

      if (!monthFirstIndices.has(monthKey)) {
        monthFirstIndices.set(monthKey, index);
      }
    });

    // Her ayın ilk gününe ay ismini ekle
    monthFirstIndices.forEach((index, monthKey) => {
      const date = new Date(allData[index].date);
      allData[index].month = getShortMonthName(date.getMonth(), locale);
    });

    return allData;
  }, [data]);

  const [selectedBar, setSelectedBar] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0 });
  const chartContainerRef = useRef(null);

  // Fiyatın geçerli olup olmadığını kontrol et
  const isValidPrice = useCallback((price) => {
    return price !== null && price !== undefined && price > 0;
  }, []);

  // chartData değiştiğinde selectedBar'ı güncelle - en ucuz fiyatı seç
  useEffect(() => {
    if (chartData && chartData.length > 0 && !selectedBar) {
      // Geçerli fiyatları filtrele
      const validPrices = chartData.filter((item) => isValidPrice(item.price));
      if (validPrices.length > 0) {
        // En ucuz fiyatı bul
        const cheapestPrice = Math.min(
          ...validPrices.map((item) => item.price),
        );
        // En ucuz fiyata sahip ilk öğeyi seç
        const cheapestItem = validPrices.find(
          (item) => item.price === cheapestPrice,
        );
        if (cheapestItem) {
          setSelectedBar(cheapestItem);
        }
      }
    }
  }, [chartData, selectedBar, isValidPrice]);

  // selectedBar değiştiğinde index 15 ve üzerindeyse sağa scroll yap
  useEffect(() => {
    if (!selectedBar) return;

    const selectedIndex = chartData.findIndex(
      (item) => item.date === selectedBar.date,
    );

    // Index 15 ve daha büyükse scroll yap
    if (selectedIndex >= DAYS_TO_SHOW / 2) {
      setTimeout(() => {
        const bar = document.getElementById(`bar-${selectedIndex}`);
        if (!bar) return;

        const container = bar.closest(".overflow-x-auto");
        if (!container) return;

        // Element'in scroll container içindeki pozisyonunu bul
        let barLeft = bar.offsetLeft;
        let parent = bar.parentElement;
        while (parent && parent !== container) {
          barLeft += parent.offsetLeft;
          parent = parent.parentElement;
        }

        const containerWidth = container.clientWidth;
        const barWidth = bar.getBoundingClientRect().width;
        const scrollPosition = barLeft - containerWidth / 2 + barWidth / 2;

        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }, 100);
    }
  }, [selectedBar, chartData]);

  // Fiyat aralığını hesapla - useMemo ile optimize et
  const { maxPrice, minPrice, priceRange } = useMemo(() => {
    const maxPrice = Math.max(...chartData.map((item) => item.price));
    const minPrice = Math.min(...chartData.map((item) => item.price));
    const priceRange = maxPrice - minPrice;
    return { maxPrice, minPrice, priceRange };
  }, [chartData]);

  // Tooltip pozisyonunu hesapla
  useEffect(() => {
    if (selectedBar) {
      const selectedIndex = chartData.findIndex(
        (item) => item.date === selectedBar.date,
      );
      if (selectedIndex !== -1) {
        // 768px altında sabit ortada, üstünde dinamik pozisyon
        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

        if (isMobile) {
          // Mobilde sabit ortada
          setTooltipPosition({ left: 0, isMobile: true, barTop: 0 });
        } else {
          // Desktop'ta dinamik pozisyon
          const selectedBarElement = document.getElementById(
            `bar-${selectedIndex}`,
          );
          if (selectedBarElement) {
            const barRect = selectedBarElement.getBoundingClientRect();
            const chartContainer = chartContainerRef.current;
            const containerRect = chartContainer.getBoundingClientRect();

            // Bar'ın container'a göre pozisyonu
            const barLeft = barRect.left - containerRect.left;
            const barTop = barRect.top - containerRect.top;
            const barBottom = barRect.bottom - containerRect.top;

            // Tooltip'i bar'ın ortasına hizala
            let left = barLeft + barRect.width / 2 - TOOLTIP_WIDTH / 2;

            // Sınırları kontrol et
            left = Math.max(left, 0);
            left = Math.min(left, containerRect.width - TOOLTIP_WIDTH);

            setTooltipPosition({ left, isMobile: false, barTop: barBottom });
          }
        }
      }
    }
  }, [selectedBar, chartData]);

  // Tarih formatla - dateFormatter helper kullan
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const dayName = dayNames[date.getDay()];
    return { day: day.toString(), dayName };
  }, []);

  // Ay isimlerini al - dateFormatter helper kullan
  const getMonthName = useCallback((dateString) => {
    const formatted = dateFormats.dayShortMonth(dateString, "tr");
    return formatted.split(" ")[1]; // "15 Oca" -> "Oca"
  }, []);

  // Ay geçişlerini tespit et - useMemo ile optimize et
  const monthTransitions = useMemo(() => {
    const transitions = [];
    let currentMonth = null;

    chartData?.forEach((item, index) => {
      const month = getMonthName(item.date);
      if (month !== currentMonth) {
        // Yeni ayın ilk gününde ay ismini göster
        transitions.push({ index: index, month }); // Aynı index'te göster
        currentMonth = month;
      }
    });

    return transitions;
  }, [chartData, getMonthName]);

  // Bar yüksekliğini hesapla (piksel cinsinden) - useCallback ile optimize et
  const getBarHeight = useCallback(
    (price) => {
      if (priceRange === 0) return 80; // Tüm fiyatlar aynıysa

      // Yüksekliği 20px ile 120px arasında hesapla (185px yüksekliğe uygun)
      const normalizedPrice = (price - minPrice) / priceRange;
      const height = normalizedPrice * 100 + 20; // Min 20px, max 120px

      return height;
    },
    [priceRange, minPrice],
  );

  // Y-axis değerlerini hesapla - useMemo ile optimize et
  const yAxisValues = useMemo(
    () => [
      maxPrice,
      maxPrice * 0.75,
      maxPrice * 0.5,
      maxPrice * 0.25,
      minPrice,
    ],
    [maxPrice, minPrice],
  );

  // Grid line ratios - useMemo ile optimize et
  const gridLineRatios = useMemo(() => [0, 0.25, 0.5, 0.75, 1], []);

  // Tooltip date format - dateFormatter helper kullan
  const formatTooltipDate = useCallback((dateString) => {
    return dateFormats.dayLongMonthShortDay(dateString, locale);
  }, [locale]);

  // Currency format - yardımcı fonksiyon kullan
  const formatPrice = useCallback(
    (amount, currencyCode = currency) => {
      return formatCurrency(amount, currencyCode, {
        showSymbol: false,
        showCode: true,
        decimals: 0,
      });
    },
    [currency],
  );

  // Handle bar click - useCallback ile optimize et
  const handleBarClick = useCallback(
    (item) => {
      // Fiyatı 0 veya null olan barlar seçilemez
      if (!isValidPrice(item.price)) return;
      setSelectedBar(item);
    },
    [isValidPrice],
  );

  // Handle search click - validateAndSearch benzeri güncellenmiş versiyon
  const handleSearchClick = useCallback(() => {
    const currentFlightPath = getLocalizedPath('/flight', locale)?.slice(1);

    if (!selectedBar || !isValidPrice(selectedBar.price)) return;

    // Gerçek URL'yi al - window.location.pathname kullan
    const actualPathname =
      typeof window !== "undefined" ? window.location.pathname : pathname;

    // URL'den şehir bilgilerini parse et
    // URL formatları:
    // - /ucak-bileti/istanbul-antalya (landing page)
    // - /flight/istanbul-antalya (landing page)
    // - /ucak-bileti/tek-yon/[airportInfo]/[date] (listing page)
    // - /flight/one-way/[airportInfo]/[date] (listing page)
    let parsedOrigin = "";
    let parsedDestination = "";

    if (actualPathname) {
      const pathSegments = actualPathname.split("/").filter(Boolean);

      // flight veya ucak-bileti segment'ini bul
      const flightIndex = pathSegments.findIndex(
        (segment) => segment === "ucak-bileti" || segment === "flight" || segment === currentFlightPath,
      );

      if (flightIndex !== -1 && flightIndex + 1 < pathSegments.length) {
        let routeSegmentIndex = flightIndex + 1;
        let routeSegment = pathSegments[routeSegmentIndex];

        // Eğer sonraki segment route type ise (tek-yon, one-way, gidis-donus, round-trip), onu atla
        const routeTypes = [
          "tek-yon",
          "one-way",
          "gidis-donus",
          "round-trip",
          "coklu-ucus",
          "multi-city",
        ];
        if (routeTypes.includes(routeSegment)) {
          routeSegmentIndex = flightIndex + 2;
          if (routeSegmentIndex < pathSegments.length) {
            routeSegment = pathSegments[routeSegmentIndex];
          } else {
            routeSegment = null;
          }
        }

        // Eğer route segment'i varsa ve içinde "-" varsa parse et
        if (routeSegment && routeSegment.includes("-")) {
          const cities = routeSegment.split("-");
          // Bazı şehir isimleri kendi içinde "-" içerebilir (örn: "sabiha-gokcen")
          // Bu durumda son segment'i destination olarak al, geri kalanını origin olarak al
          if (cities.length >= 2) {
            // Son segment destination, geri kalanı origin
            parsedDestination = cities[cities.length - 1];
            parsedOrigin = cities.slice(0, -1).join("-");
          }
        }
      }
    }

    // URL'den parse edilen şehir isimlerini kullan
    if (!parsedOrigin || !parsedDestination) {
      console.error("URL'den şehir bilgileri parse edilemedi:", actualPathname);
      return;
    }

    // URL oluştur - URL'den parse edilen şehir bilgilerini kullan
    // airportInfo formatı: sadece şehir isimleri {originId}-{destinationId}
    const airportInfo = `${parsedOrigin}-${parsedDestination}`;
    const depDate = selectedBar.date; // Zaten YYYY-MM-DD formatında

    // Locale'e göre URL path'ini belirle
    // Türkçe: /ucak-bileti/tek-yon, diğer diller: /flight/one-way
    const oneWayPath =
      locale === "tr" ? "ucak-bileti/tek-yon" : "flight/one-way";

    // Query parametreleri oluştur - mevcut query parametrelerini koru
    const queryParams = new URLSearchParams(searchParams.toString());

    // Adult bilgisini ekle (varsayılan 1)
    queryParams.set("adult", "1");

    const queryString = queryParams.toString();
    const baseUrl = `/${oneWayPath}/${airportInfo}/${depDate}`;
    const flightLink = queryString ? `${baseUrl}?${queryString}` : baseUrl;

    // Direkt yönlendirme yap - sayfa yüklendiğinde scroll pozisyonu korunmasın
    router.push(flightLink, undefined, { scroll: true });
  }, [selectedBar, pathname, searchParams, router, isValidPrice, locale]);

  // Loading state check
  if (!chartData || chartData.length === 0) {
    return (
      <div className="w-full py-8 text-center text-gray-500">
        {tFlight("dataLoading")}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Tooltip alanı - Chart'ın üstünde */}
      <div className="relative h-32 w-full">
        {selectedBar && (
          <>
            {/* Kesikli çizgi */}
            {/*  <div
              className="border-primary absolute z-40 border-l-4 border-dashed"
              style={{
                left: tooltipPosition.isMobile
                  ? "50%"
                  : `${tooltipPosition.left + 18}px`, // Bar'ın ortası
                top: tooltipPosition.isMobile ? "120px" : "91px", // Bar'ın üst noktasından başla
                height: tooltipPosition.isMobile
                  ? "200px"
                  : `${120 - tooltipPosition.barTop}px`, // Tooltip'in altına kadar
                transform: tooltipPosition.isMobile
                  ? "translateX(-50%)"
                  : "none",
              }}
            /> */}

            {/* Tooltip */}
            <div
              className="absolute top-[13px] z-[2] w-full space-y-2 rounded-lg bg-white p-4 shadow-lg sm:max-w-[300px]"
              style={{
                left: tooltipPosition.isMobile
                  ? "50%"
                  : `${tooltipPosition.left}px`,
                top: "20px",
                transform: tooltipPosition.isMobile
                  ? "translateX(-50%)"
                  : "none",
              }}
            >
              <div className="flex flex-row items-center justify-start gap-1">
                {" "}
                <div className="text-foreground text-base font-normal">
                  {tFlight("cheapestFlights")}{" "}
                </div>
                <div className="text-foreground text-base font-medium">
                  {formatTooltipDate(selectedBar.date)}
                </div>
              </div>

              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center justify-between gap-2">
                  <span className="text-foreground text-base font-normal">
                    {tFlight("startingPrice")}
                  </span>
                  <span className="text-large text-primary  font-semibold">
                    {formatPrice(selectedBar.price, currency)}
                  </span>
                </div>
                <Button
                  onClick={handleSearchClick}
                  disabled={!isValidPrice(selectedBar.price)}
                  className="flex h-[36px] flex-row items-center px-4 disabled:cursor-not-allowed disabled:opacity-50 lg:px-4"
                >
                  <span className="text-sm">{tFlight("search")}</span>
                </Button>
              </div>
              <span className="absolute bottom-0 left-0 flex h-1 w-full items-center justify-center">
                <span className="relative z-[1] h-3 w-3 rotate-45 bg-white"></span>
                <span className="border-l-primary absolute top-full ml-[1px] h-[120px] w-0 border-l border-dashed"></span>
              </span>
            </div>
          </>
        )}
      </div>

      {/* Y ekseni etiketleri */}
      <div
        ref={chartContainerRef}
        className="relative flex h-[175px] w-full items-end justify-between"
      >
        {/* Y ekseni değerleri - Sabit */}
        <div className="-mt-2 flex h-[73%] flex-col gap-2 self-start pr-4 text-xs text-gray-500">
          {yAxisValues.map((value, index) => (
            <div key={index} className="flex min-w-16 items-start">
              <span className="text-foreground mr-2 text-sm font-semibold">
                {formatPrice(value, currency)}
              </span>
            </div>
          ))}
        </div>
        <div></div>
        {/* Barlar - Scrollable */}
        <div className="scrollbar-hide flex h-full min-w-[500px] flex-1 overflow-x-auto">
          <div className="relative flex h-full w-full items-end justify-between gap-1">
            {/* Yatay grid çizgileri */}
            <div className="pointer-events-none absolute inset-0 flex h-[66%] flex-col justify-between self-start">
              {gridLineRatios.map((ratio, index) => (
                <div
                  key={index}
                  className="w-full border-b border-[#909294]"
                  style={{ opacity: 0.3 }}
                />
              ))}
            </div>

            {/* Barlar */}
            <div className="relative z-10 flex h-full w-full items-end justify-between gap-1.5">
              {chartData?.map((item, index) => {
                const isSelected =
                  selectedBar && selectedBar.date === item.date;
                const dateInfo = formatDate(item.date);
                const barHeight = getBarHeight(item.price);
                const hasValidPrice = isValidPrice(item.price);

                return (
                  <div
                    key={index}
                    id={`bar-${index}`}
                    className="flex h-full flex-col items-center justify-end"
                  >
                    {/* Bar */}
                    <div
                      className={clsx(
                        "transition-all duration-200",
                        !hasValidPrice
                          ? "cursor-not-allowed opacity-40"
                          : "cursor-pointer",
                        isSelected
                          ? "bg-[#01007F]"
                          : hasValidPrice
                            ? "bg-[#C9C8E3] hover:bg-[#A1A0D0]"
                            : "bg-[#C9C8E3]",
                        "w-[16.8px] sm:w-8",
                      )}
                      style={{
                        height: `${barHeight}px`,
                        minHeight: "20px", // Minimum yükseklik artırıldı
                        borderTopLeftRadius: "4px",
                        borderTopRightRadius: "4px",
                        borderBottomLeftRadius: "0px",
                        borderBottomRightRadius: "0px",
                      }}
                      onClick={() => handleBarClick(item)}
                      role={hasValidPrice ? "button" : undefined}
                      tabIndex={hasValidPrice ? 0 : -1}
                      aria-label={`${dateInfo.day} ${dateInfo.dayName} - ${formatPrice(item.price, currency)}`}
                      aria-disabled={!hasValidPrice}
                      onKeyDown={(e) => {
                        if (!hasValidPrice) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleBarClick(item);
                        }
                      }}
                    />

                    {/* Tarih etiketleri */}
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="pt-1">
                        <div className="text-foreground text-center text-xs font-medium">
                          {dateInfo.day}
                        </div>
                        <div className="text-foreground-subtle text-center text-xs">
                          {dateInfo.dayName}
                        </div>
                      </div>
                      <span className="text-foreground-muted block h-4 text-center text-sm font-medium">
                        {item.month}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
